import { FC, useMemo } from "react";
import { Button } from "@/components/button";
import { ColumnDef } from "@tanstack/react-table";
import { Table } from "@/components/table/table";
import { UuidFieldRow } from "@/components/table/uuid-field-row";
import { TextFieldRow } from "@/components/table/text-field-row";
import { DateTimeFieldRow } from "@/components/table/datetime-field-row";
import { DateTime } from "luxon";
import { orderBy } from "lodash-es";
import styled from "@emotion/styled";
import { AudioRecordingSummaryInternalResponseSchema } from "@/pages/audio-recordings/shared/api";

interface AudioRecordingsTableProps {
  data: AudioRecordingSummaryInternalResponseSchema[];
  onViewAudioRecording: (id: string) => void;
  pageSize: number;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

const StatusBadge = styled.span<{ status: string }>`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  background-color: ${({ status }) => {
    switch (status) {
      case "COMPLETED":
        return "#dcfce7";
      case "IN_PROGRESS":
        return "#fef3c7";
      case "ERROR":
        return "#fee2e2";
      default:
        return "#f3f4f6";
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case "COMPLETED":
        return "#166534";
      case "IN_PROGRESS":
        return "#92400e";
      case "ERROR":
        return "#991b1b";
      default:
        return "#374151";
    }
  }};
`;

export const AudioRecordingsTable: FC<AudioRecordingsTableProps> = ({
  data,
  onViewAudioRecording,
  pageSize,
  onLoadMore,
  hasMore = false,
}) => {
  const columns = useMemo<ColumnDef<AudioRecordingSummaryInternalResponseSchema>[]>(
    () => [
      {
        header: "Recording ID",
        cell: ({ row }) => <UuidFieldRow id={row.original.id} />,
      },
      {
        header: "Space Account",
        cell: ({ row }) => <UuidFieldRow id={row.original.owned_by_space_account_id} />,
      },
      {
        header: "Title",
        cell: ({ row }) => <TextFieldRow value={row.original.title ?? "—"} />,
      },
      {
        header: "Recording",
        cell: ({ row }) => (
          <StatusBadge status={row.original.recording_status}>
            {row.original.recording_status}
          </StatusBadge>
        ),
      },
      {
        header: "Upload",
        cell: ({ row }) => (
          <StatusBadge status={row.original.upload_status}>
            {row.original.upload_status}
          </StatusBadge>
        ),
      },
      {
        header: "Processing",
        cell: ({ row }) => (
          <StatusBadge status={row.original.processing_status}>
            {row.original.processing_status}
          </StatusBadge>
        ),
      },
      {
        header: "Modified At",
        cell: ({ row }) => <DateTimeFieldRow dateStr={row.original.locally_modified_at} />,
      },
      {
        header: "View",
        cell: ({ row }) => (
          <Button size="1" onClick={() => onViewAudioRecording(row.original.id)}>
            View
          </Button>
        ),
      },
    ],
    [onViewAudioRecording]
  );

  const sortedData = useMemo(() => {
    return orderBy(
      data,
      x => (x.locally_modified_at ? DateTime.fromISO(x.locally_modified_at).toMillis() : -Infinity),
      "desc"
    );
  }, [data]);

  return (
    <Table
      data={sortedData}
      columns={columns}
      pageSize={pageSize}
      onLoadMore={onLoadMore}
      showLoadMore={hasMore}
      loadMoreLabel="Load More"
    />
  );
};
